import { formatMoney, PageError, PageLoading } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { amountText, scopeText } from "../../pricing";
import { breakdown, type PriceLine } from "../../zones";
import styles from "../customers/customers.module.css";

/** Where a place's price is set: the place, the business, the zone, or the price for any zone. */
function SetAt({ scope, zoneName }: { scope: PriceLine["scope"]; zoneName: string }) {
  switch (scope) {
    case "location":
      return <Badge tone="info">This place</Badge>;
    case "business":
    case "customer":
      return <Badge tone="info">Business</Badge>;
    case "zone":
      return <Badge>{zoneName}</Badge>;
    default:
      return <Badge>Any zone</Badge>;
  }
}

/**
 * What the business pays at each of its places, item by item, and the rules set for it. A place's
 * own price wins, then the business's, then the zone's (SCHEMA.md → Pricing).
 */
export function BusinessPrices({ businessId }: { businessId: string }): ReactNode {
  const format = useCompanyFormat();
  const sheet = useQuery({
    queryKey: ["pricing", "place-sheet", businessId],
    queryFn: () => ok(api.GET("/v1/admin/pricing/place-sheet", { params: { query: { business_id: businessId } } })),
  });
  const rules = useQuery({
    // not ["pricing", "rules", …]: that's the Pricing page's paged list, whose cache holds pages
    queryKey: ["pricing", "business-rules", businessId],
    queryFn: () =>
      ok(
        api.GET("/v1/admin/pricing", {
          params: { query: { business_id: businessId, active_at: new Date().toISOString(), limit: 100 } },
        }),
      ),
  });
  if (sheet.status === "pending") return <PageLoading label="Loading prices" />;
  if (sheet.status === "error")
    return <PageError message={messageFor(errorOf(sheet.error))} onRetry={() => sheet.refetch()} />;
  const money = (v: string) => formatMoney(v, { ...format, currency: sheet.data.currency });
  const nameOf = new Map(sheet.data.items.map((i) => [i.id, i.name]));

  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <span className={styles.muted}>
          A place's own price wins, then the business's, then the zone's. One of each item, at today's prices.
        </span>
        <Link to="/pricing" search={{ business_id: businessId }} className={styles.link}>
          Edit in Pricing
        </Link>
      </div>
      {sheet.data.places.length === 0 ? <p className={styles.muted}>The business has no places yet.</p> : null}
      {sheet.data.places.map((place) => (
        <section key={place.location_id} className={styles.card} aria-label={`Prices at ${place.label}`}>
          <div className={styles.cardHead}>
            <div className={styles.stack}>
              <span className={styles.title}>{place.label}</span>
              <span className={styles.muted}>
                {[place.address_line, place.zone?.name ?? "Outside every zone"].filter(Boolean).join(" · ")}
              </span>
            </div>
          </div>
          {place.served ? (
            <SortableDataTable
              caption={`Prices at ${place.label}`}
              rowKey="product_item_id"
              rows={place.items}
              itemName={{ one: "item", other: "items" }}
              emptyMessage="No items can be ordered yet"
              columns={[
                {
                  key: "product_item_id",
                  label: "Item",
                  sortable: false,
                  render: (_v, c) => nameOf.get(c.product_item_id) ?? "—",
                },
                {
                  key: "price",
                  label: "Price",
                  sortable: false,
                  render: (_v, c) => {
                    const { price } = breakdown(c.lines);
                    return c.missing || !price ? (
                      <span className={styles.low}>No price</span>
                    ) : (
                      money(price.unit_amount)
                    );
                  },
                },
                {
                  key: "set_at",
                  label: "Set at",
                  sortable: false,
                  render: (_v, c) => {
                    const { price } = breakdown(c.lines);
                    return price ? <SetAt scope={price.scope} zoneName={place.zone?.name ?? "Zone"} /> : null;
                  },
                },
                {
                  key: "receivable_total",
                  label: "Customer pays",
                  sortable: false,
                  render: (_v, c) => (c.receivable_total ? money(c.receivable_total) : "—"),
                },
                {
                  key: "margin",
                  label: "Margin",
                  sortable: false,
                  render: (_v, c) => (c.margin ? money(c.margin) : "—"),
                },
              ]}
            />
          ) : (
            <Alert tone="warning" title="Not served">
              No active service zone covers this place, so it can't be priced or ordered to.
            </Alert>
          )}
        </section>
      ))}
      <section className={styles.card} aria-label="Rules for this business">
        <span className={styles.title}>Rules for this business, in force now</span>
        {rules.error ? (
          <Alert tone="danger" title="Rules didn't load">
            {messageFor(errorOf(rules.error))}
          </Alert>
        ) : (
          <SortableDataTable
            caption="Rules for this business"
            rowKey="id"
            rows={rules.data?.data ?? []}
            itemName={{ one: "rule", other: "rules" }}
            emptyMessage="None: its places take their zone's prices"
            columns={[
              {
                key: "charge_type",
                label: "Charge",
                sortable: false,
                render: (_v, r) => (
                  <Link to="/pricing/$ruleId" params={{ ruleId: r.id }} className={styles.link}>
                    {r.charge_type.name}
                  </Link>
                ),
              },
              {
                key: "scope",
                label: "Applies to",
                sortable: false,
                render: (_v, r) => <span className={styles.muted}>{scopeText(r)}</span>,
              },
              {
                key: "amount",
                label: "Amount",
                sortable: false,
                render: (_v, r) => amountText(r, (v) => formatMoney(v, { ...format, currency: r.currency })),
              },
            ]}
          />
        )}
      </section>
    </div>
  );
}
